import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit3, Loader2 } from 'lucide-react';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { BlogEditor, BlogEditorPayload } from '../components/blog/BlogEditor';
import { BlogPost } from '../types/blog';
import useFetchBlogDetailsById from "../hooks/Blog/useFetchBlogDetailsById";
import { useUpdateBlog } from '../hooks/Blog/useUpdateBlog';
import Breadcrumb from '../components/Breadcrumbs';
import { toast } from 'react-toastify';
import Preloader from '../components/ui/Preloader';

interface BreadcrumbItem {
  label: string;
  href: string;
  active?: boolean;
}

interface RouteParams extends Record<string, string | undefined> {
  blogId: string;
}

const BlogEditPage: React.FC = () => {
  const { blogId } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { post, loading, isLoaded, error } = useFetchBlogDetailsById(blogId || '');
  const { updatePost, loading: updating } = useUpdateBlog();

  const persist = async (
    postData: BlogEditorPayload,
    successMessage: string,
    failureMessage: string
  ): Promise<boolean> => {
    if (!blogId) {
      toast.error('Blog ID is missing', { position: 'top-right', autoClose: 3000 });
      return false;
    }

    try {
      const success = await updatePost(blogId, postData as Partial<BlogPost>);
      if (success) {
        toast.success(successMessage, { position: 'top-right', autoClose: 3000 });
        return true;
      }

      toast.error(failureMessage, { position: 'top-right', autoClose: 5000 });
      return false;
    } catch (err) {
      console.error('Update error:', err);
      toast.error('An error occurred while saving', { position: 'top-right', autoClose: 5000 });
      return false;
    }
  };

  const handleSave = (postData: BlogEditorPayload): Promise<boolean> =>
    persist(postData, 'Draft saved successfully', 'Failed to save draft');

  const handleSubmit = async (postData: BlogEditorPayload): Promise<boolean> => {
    const success = await persist(
      postData,
      'Blog post updated successfully!',
      'Failed to update blog post'
    );

    if (success) {
      setTimeout(() => navigate('/practice/blog'), 500);
    }

    return success;
  };

  const handleGoBack = (): void => {
    navigate('/practice/blog');
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "Dashboard", href: "/practice" },
    { label: "Blog", href: "/practice/blog" },
    { label: `Edit: ${post?.title || 'Loading...'}`, href: `/practice/blog/edit/${blogId}`, active: true },
  ];

  const backButton = (
    <button
      type="button"
      onClick={handleGoBack}
      className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all duration-200 shadow-sm"
    >
      <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
      Back to Blog
    </button>
  );

  const statusBadge = (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
      post?.status === 'published'
        ? 'bg-green-100 text-green-800'
        : post?.status === 'pending_review'
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-gray-100 text-gray-800'
    }`}>
      {post?.status === 'published' && '🟢'}
      {post?.status === 'pending_review' && '🟡'}
      {post?.status === 'draft' && '⚪'}
      <span className="ml-1 capitalize">
        {post?.status ? post.status.replace('_', ' ') : 'unknown'}
      </span>
    </span>
  );

  /** Breadcrumbs on the left, status + back control on the right — identical on every branch. */
  const headerRow = (withStatus: boolean) => (
    <div className="flex items-center justify-between gap-4 mb-4">
      <div className="hidden sm:block">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="flex items-center gap-3 ml-auto">
        {withStatus && statusBadge}
        {backButton}
      </div>
    </div>
  );

  // Error state
  if (error) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
            {headerRow(false)}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl border border-red-200 p-8 text-center"
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Edit3 className="h-8 w-8 text-red-600" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Failed to Load Blog Post
              </h1>
              <p className="text-gray-600 mb-6">
                The blog post could not be found or there was an error loading it.
              </p>
              {backButton}
            </motion.div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-6 sm:mb-8"
            >
              {headerRow(false)}

              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Edit3 className="h-5 w-5 sm:h-6 sm:w-6 text-white animate-pulse" aria-hidden="true" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">
                    Loading Blog Post...
                  </h1>
                  <p className="mt-1 text-sm sm:text-base text-gray-600">
                    Please wait while we fetch your blog post
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-center mb-8">
                  <div className="flex items-center space-x-3">
                    <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                    <span className="text-lg font-medium text-gray-700">Loading blog post...</span>
                  </div>
                </div>

                <div className="animate-pulse space-y-6">
                  <div className="h-12 bg-gray-200 rounded-lg"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                  <div className="h-64 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!isLoaded) {
    return <Preloader />;
  }

  // No post found state
  if (!post) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
            {headerRow(false)}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center"
            >
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Edit3 className="h-8 w-8 text-gray-600" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Blog Post Not Found
              </h1>
              <p className="text-gray-600 mb-6">
                The blog post you're looking for doesn't exist or may have been deleted.
              </p>
              {backButton}
            </motion.div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Main edit view
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-7xl">

        {updating && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <div className="flex items-center space-x-4">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Updating Blog Post</h3>
                  <p className="text-sm text-gray-600">Please wait while we save your changes...</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="mb-6 sm:mb-8"
        >
          {headerRow(true)}

          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Edit3 className="h-5 w-5 sm:h-6 sm:w-6 text-white" aria-hidden="true" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">
                Edit Blog Post
              </h1>
              <p className="mt-1 text-sm sm:text-base text-gray-600 truncate">
                {post?.title || 'Untitled Post'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Editor */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
        >
          <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <BlogEditor
              key={`${blogId}-${post?.id || post?._id}`}
              mode="edit"
              initialPost={post}
              onSave={handleSave}
              onSubmit={handleSubmit}
              loading={updating}
              readOnly={post.status === 'pending_review' || post.status === 'published'}
            />
          </div>
        </motion.div>

        <div className="h-16 sm:h-8" />
      </div>
    </div>
  );
};

export default BlogEditPage;
